/**
 * "Assessment due" reminder — the in-app half of the reminder system.
 * Self-contained (reads growth config + the viewer's own status), rendered
 * on the directory home next to CongratsInbox. Dismiss lasts for the
 * session only; a due date is the kind of thing that should come back.
 */
import { useState } from 'react';
import { ClipboardList, X, ChevronRight } from 'lucide-react';
import { useGrowthVersion } from '../../services/growth/events';
import { getGrowthConfig, getCycleId } from '../../services/growth/configService';
import { getStatusFor } from '../../services/growth/assessmentsService';
import { shouldShowReminder, dueLabel, isOverdue } from './logic/dueDates';

export function GrowthBanner({ currentUserId, onOpen }) {
  useGrowthVersion();
  const [dismissed, setDismissed] = useState(false);

  if (!currentUserId || dismissed) return null;

  const config = getGrowthConfig();
  const status = getStatusFor(currentUserId, getCycleId());
  if (!shouldShowReminder({ config, status })) return null;

  const overdue = isOverdue(config.cycle.assessmentDue);
  const label = dueLabel(config.cycle.assessmentDue);

  return (
    <div
      className={`mb-4 flex items-center gap-3 rounded-card border px-3.5 py-2.5 shadow-e1
        ${overdue
          ? 'border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10'
          : 'border-accent-line bg-accent-wash'}`}
    >
      <span className={`w-9 h-9 rounded-card inline-flex items-center justify-center flex-none ${overdue ? 'bg-red-100 dark:bg-red-500/15' : 'bg-surface'}`}>
        <ClipboardList size={17} className={overdue ? 'text-red-600 dark:text-red-400' : 'text-accent'} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-bold text-ink leading-tight">
          Your {getCycleId()} self-assessment — {label.toLowerCase()}
        </p>
        <p className="text-[11.5px] text-ink-3">
          {status === 'in_progress' ? 'Pick up where you left off.' : 'It takes about 20 minutes.'}
        </p>
      </div>
      <button
        type="button"
        onClick={onOpen}
        className="inline-flex items-center gap-1 h-8 px-3 rounded-ctl bg-accent text-[var(--on-accent)] text-[12px] font-bold hover:opacity-90 flex-none"
      >
        {status === 'in_progress' ? 'Continue' : 'Start'} <ChevronRight size={13} />
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss reminder"
        className="w-7 h-7 rounded inline-flex items-center justify-center text-ink-4 hover:text-ink-2 flex-none"
      >
        <X size={14} />
      </button>
    </div>
  );
}
